import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { cn, extractDate, htParse } from "@/lib/utils";
import { AiAskRequest, AiAskResponse } from "@/provider/ai/type";
import { OptionsContext } from "@/provider/options-provider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Rating } from "./rating";
import { Textarea } from "./ui/textarea";
import { useToast } from "./ui/use-toast";
import Markdown from "react-markdown";

const FormSchema = z.object({
  question: z.string().min(2, {
    message: "You must type in a question.",
  }),
});

export const AskItemForm = ({ id }: { id: string }) => {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      question: "",
    },
  });
  const [answer, setAnswer] = useState<AiAskResponse | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const { searchOptions, searchText } = useContext(OptionsContext);
  const { toast } = useToast();

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    setLoading(true);
    setAnswer(undefined);
    const request: AiAskRequest = {
      ids: [id],
      system_prompt: searchOptions.systemPrompt,
      prompt: form.getValues("question"),
      keywords: [searchText],
      ticker: searchOptions.ticker,
      period: `${extractDate(searchOptions.dateRange?.from)} to ${extractDate(
        searchOptions.dateRange?.to,
      )}`,
    };
    try {
      const response = await fetch("/api/ask", {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
        },
        cache: "default",
      });
      const result: AiAskResponse = await response.json();
      setAnswer(result);
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to ask question",
        variant: "destructive",
      });
      return;
    }
  }

  return (
    <div className="mb-3 w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="w-full space-y-6"
        >
          <FormField
            control={form.control}
            name="question"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea
                    placeholder="Type a question and hit enter..."
                    {...field}
                    onKeyDown={(e) => {
                      // Shift enter to new line and enter to submits
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        form.handleSubmit(onSubmit)();
                      }
                    }}
                  />
                </FormControl>
                <FormDescription className="text-xs">
                  Ask a question about this document. Press ⏎ to submit and ⇧ +
                  ⏎ for new line.
                </FormDescription>
                <FormMessage className="text-xs" />
              </FormItem>
            )}
          />
        </form>
        <div>
          <div className="mt-2 w-full rounded-md bg-white p-4">
            {answer && !loading ? (
              <>
                <Markdown className="text-wrap px-2 text-sm">
                  {answer.generated_summary}
                </Markdown>
                {answer?.hint && (
                  <p className="text-xsm text-slate-400">{answer?.hint}</p>
                )}
                {answer?.model && (
                  <p className="text-xsm text-slate-400">
                    {answer?.model} was used.
                  </p>
                )}
              </>
            ) : (
              <code className={cn("", loading ? "animate-ping" : "")}>...</code>
            )}
          </div>
        </div>
        <div className="">
          {answer?.id && (
            <Rating
              rating={0}
              feedbackId={answer.id!}
              setFeedbackId={(id) => {
                setAnswer({ ...answer, id: id });
              }}
              model={answer.model}
            />
          )}
        </div>
      </Form>
    </div>
  );
};
